import { useEffect, useRef, type RefObject, type ReactNode } from 'react';

/**
 * 모달이 열려 있는 동안 챙겨야 하는 것들 - Esc 로 닫기, 뒤쪽 스크롤 잠그기,
 * 열릴 때 패널로 초점 옮기기.
 *
 * 아래 Modal 과, 자기 레이아웃을 직접 짜야 해서 Modal 을 못 쓰는 폼 모달
 * (offline/SalesPage 의 정산 입력처럼 sticky 머리말·바닥글이 필요한 것)이
 * 같이 쓴다 - 손으로 적으면 매번 이 셋 중 하나가 빠졌다.
 */
export function useDialogBehavior<T extends HTMLElement>(onClose: () => void): RefObject<T | null> {
  const panel = useRef<T>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return panel;
}

/**
 * 모달 한 겹.
 *
 * 랜덤박스 네 화면이 `modal-overlay` → `modal-panel` → 닫기 버튼 → 제목 묶음을
 * 손으로 다섯 벌 복사해 쓰고 있었다. 마크업이 같으니 한 곳에 모은다 - 대신
 * 손으로 적을 때 매번 빠졌던 것들을 여기서 한 번에 챙긴다.
 *
 *   - **Esc 로 닫힌다.** 예전 모달들은 배경을 눌러야만 닫혔다.
 *   - **바깥 스크롤을 막는다.** 모달을 열어 둔 채 휠을 굴리면 뒤쪽 목록이
 *     따라 움직였다.
 *   - **열릴 때 패널로 초점을 옮긴다.** 키보드만 쓰는 사람이 Tab 을 눌렀을 때
 *     모달 안이 아니라 뒤쪽 표의 버튼들을 훑던 문제.
 *   - 배경 클릭으로 닫는 동작과 `stopPropagation` 은 종전과 같다.
 */
export function Modal({
  title,
  onClose,
  children,
  wide,
  titleExtra,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** 표가 들어가는 모달(고객별 당첨 이력 등). */
  wide?: boolean;
  /** 제목 옆에 작게 붙는 부제 - 회원명·번호처럼 대상을 밝히는 자리다. */
  titleExtra?: ReactNode;
}) {
  const panel = useDialogBehavior<HTMLDivElement>(onClose);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        ref={panel}
        className={wide ? 'modal-panel modal-panel--wide' : 'modal-panel'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">
          {title}
          {titleExtra ? <span className="page-title__sub">{titleExtra}</span> : null}
        </h2>
        {children}
      </div>
    </div>
  );
}

/**
 * 되돌릴 수 없는 일을 하기 전에 한 번 묻는 모달.
 *
 * 경품 삭제는 표 안에서 버튼이 '삭제' → '정말 삭제' 로 바뀌는 방식이었다.
 * 그 자리에서 바로 확인되는 것은 빠르지만, 같은 줄의 '수정'·'복사' 와 붙어
 * 있어서 잘못 누르기 쉽다. 지우는 것만 이 모달로 옮긴다.
 */
export function ConfirmModal({
  title,
  message,
  confirmLabel = '삭제',
  danger = true,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  /** 실패했을 때 보여줄 문구. 성공하면 호출부가 모달을 닫는다. */
  error?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <p className={danger ? 'hint hint--warn' : 'hint'}>{message}</p>
      {error ? <div className="state state--error" role="alert">{error}</div> : null}
      <div className="editor__actions">
        <button type="button" className="btn btn--ghost" onClick={onClose} disabled={pending}>
          취소
        </button>
        <button
          type="button"
          className={danger ? 'btn btn--danger' : 'btn btn--primary'}
          onClick={onConfirm}
          disabled={pending}
        >
          {pending ? '처리 중…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
